import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFormattedDateTime } from "../../assets/Utils";
import { listVitalsWatchFindings } from '../../utils/vitalsWatch';

const VitalsWidget = ({ admissionId }) => {
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = React.useRef(true);

  const fetchVitals = async () => {
    try {
      const response = await axios.get( `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/vitals-logs?admission_id=${admissionId}`);
      if (isMounted.current) {
        setVitals(response.data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching vitals:', err);
      if (isMounted.current) {
        setError('Failed to load vitals');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (admissionId) {
      fetchVitals();
      
      const intervalId = setInterval(() => {
        if (isMounted.current) {
          fetchVitals();
        }
      }, 5 * 60 * 1000); // 5 minutes

      return () => {
        isMounted.current = false;
        clearInterval(intervalId);
      };
    }
  }, [admissionId]);

  if (loading) return <div className="vitals-widget">Loading vitals...</div>;
  if (error) return <div className="vitals-widget">Error: {error}</div>;
  if (!vitals || vitals.length === 0) {
    return (
      <div className="vitals-widget">
        <h4>Latest Vitals</h4>
        <p>No vitals recorded</p>
      </div>
    );
  }

  const latestVital = vitals[0];
  const vitalDate = new Date(latestVital.createdAt);
  const now = new Date();
  const hoursDiff = (now - vitalDate) / (1000 * 60 * 60);
  const watchFindings = listVitalsWatchFindings(latestVital);
  
  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  return (
    <div className="vitals-widget">
      <h4>Latest Vitals</h4>
      
      {/* Time status message */}
      <div className={`time-status ${hoursDiff > 4 ? 'overdue' : ''}`}>
        {hoursDiff > 4 ? (
          <span>Vitals are over 4 hours old!</span>
        ) : (
          <span>Taken at: {getFormattedDateTime(latestVital.createdAt)}</span>
        )}
      </div>

      {/* Vitals score section */}
      <div className="vitals-score-container">
        <div 
          className="risk-indicator"
          style={{ backgroundColor: getRiskColor(latestVital.vitals_score_risk) }}
        >
          {latestVital.vitals_score_risk || 'N/A'}
        </div>
        
        <div className="score-details">
          <div className="score-value">
            Score: <strong>{latestVital.vitals_score || 'N/A'}</strong>
          </div>
          <div className="critical-count">
            Critical Vitals: <strong>{latestVital.vitals_score_criticals_count || 0}</strong>
          </div>
        </div>
      </div>
      {watchFindings.length > 0 && (
        <div className="vitals-watch">
          {watchFindings.map((finding) => finding.label.replace(' · ', ' ')).join('; ')}
        </div>
      )}
    </div>
  );
};

export default VitalsWidget;
